import toast from "react-hot-toast";
import { api, AUTH_TOKEN_CLEARED_EVENT } from "../AppContext";
import { createSseParser } from "./sse";
import {
  errorCodeFromStatus,
  parseStreamEvent,
  type AssistantErrorCode,
  type AssistantStreamEvent,
  type UiHistoryMessage,
} from "./assistant-messages";
import type { AssistantPageContext } from "./assistant-links";
import { parseAssistantCapabilities, type AssistantCapabilities } from "./assistant-access";

export class AssistantRequestError extends Error {
  code: AssistantErrorCode;
  status: number;

  constructor(code: AssistantErrorCode, message: string, status = 0) {
    super(message);
    this.name = "AssistantRequestError";
    this.code = code;
    this.status = status;
  }
}

export interface AssistantStatus {
  enabled: boolean;
  model: string;
  capabilities: Partial<AssistantCapabilities> | null;
}

export interface AssistantConversation {
  conversationId: string;
  messages: UiHistoryMessage[];
}

export interface StreamChatOptions {
  message: string;
  conversationId?: string | null;
  context?: AssistantPageContext;
  signal?: AbortSignal;
  onEvent: (event: AssistantStreamEvent) => void;
}

function apiBaseUrl(): string {
  return String(import.meta.env.VITE_API_URL || "").replace(/\/+$/, "");
}

export function clearSessionAfterUnauthorized() {
  const hadToken = !!localStorage.getItem("token");
  localStorage.removeItem("token");
  api.defaults.headers.common["Authorization"] = "";
  window.dispatchEvent(new Event(AUTH_TOKEN_CLEARED_EVENT));
  if (hadToken && window.location.pathname !== "/") {
    toast.error("Your session expired — please sign in again");
  }
}

async function readErrorBody(response: Response): Promise<{ message: string; code?: unknown }> {
  try {
    const text = await response.text();
    if (!text) return { message: "" };
    try {
      const data = JSON.parse(text);
      return {
        message: typeof data?.error === "string" ? data.error : data?.message || "",
        code: data?.code,
      };
    } catch {
      return { message: text.slice(0, 300) };
    }
  } catch {
    return { message: "" };
  }
}

export function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error as any)?.name === "AbortError"
  );
}

export async function streamChat({
  message,
  conversationId,
  context,
  signal,
  onEvent,
}: StreamChatOptions): Promise<void> {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    "X-Locale": "en-sa",
  };
  if (token) headers.Authorization = token;

  const body: Record<string, unknown> = { message };
  if (conversationId) body.conversationId = conversationId;
  if (context) body.context = context;

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl()}/assistant/chat`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal,
      cache: "no-store",
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new AssistantRequestError("network", "Network error", 0);
  }

  if (!response.ok) {
    const { message: errorMessage, code } = await readErrorBody(response);
    if (response.status === 401) clearSessionAfterUnauthorized();
    throw new AssistantRequestError(
      errorCodeFromStatus(response.status, code),
      errorMessage || `Request failed (${response.status})`,
      response.status,
    );
  }

  if (!response.body) {
    throw new AssistantRequestError("interrupted", "Empty response from server", response.status);
  }

  let finished = false;
  const parser = createSseParser({
    onEvent: ({ event, data }) => {
      const parsed = parseStreamEvent(event, data);
      if (!parsed) return;
      if (parsed.type === "done" || parsed.type === "error") finished = true;
      onEvent(parsed);
    },
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
      if (finished) break;
    }
    parser.push(decoder.decode());
    parser.flush();
  } catch (error) {
    if (isAbortError(error) || signal?.aborted) throw error;
    if (!finished) {
      throw new AssistantRequestError("interrupted", "The connection was interrupted", 0);
    }
  } finally {
    if (finished) reader.cancel().catch((): void => {});
  }

  if (!finished) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    throw new AssistantRequestError("interrupted", "The response ended unexpectedly", 0);
  }
}

export async function getAssistantStatus(signal?: AbortSignal): Promise<AssistantStatus> {
  const res = await api.get("/assistant/status", { signal, silent: true } as any);
  return {
    enabled: !!res.data?.enabled,
    model: typeof res.data?.model === "string" ? res.data.model : "",
    capabilities: parseAssistantCapabilities(res.data?.capabilities),
  };
}

export async function getConversation(
  conversationId: string,
  signal?: AbortSignal,
): Promise<AssistantConversation | null> {
  try {
    const res = await api.get(`/assistant/conversations/${encodeURIComponent(conversationId)}`, {
      signal,
      silent: true,
    } as any);
    return {
      conversationId: res.data?.conversationId || conversationId,
      messages: Array.isArray(res.data?.messages) ? res.data.messages : [],
    };
  } catch (error: any) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

export async function deleteConversation(conversationId: string): Promise<void> {
  try {
    await api.delete(`/assistant/conversations/${encodeURIComponent(conversationId)}`, {
      silent: true,
    } as any);
  } catch (error: any) {
    if (error?.response?.status === 404) return;
    throw error;
  }
}
